import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { color } from 'styles/colors'

import Api from 'services/api'
import Honeybadger from 'honeybadger-js'
import { useSessionContext } from 'hooks/SessionContext'
import useChatbotTermsApi from 'hooks/useChatbotTermsApi'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import Modal from 'components/Modal'
import ChatbotTermsAndConditions from 'pages/Chatbot/TermsAndConditions/ChatbotTermsAndConditions'

type Props = {
  showTermsInFooter: boolean
  onCancelCallback?: () => void
}

const TermsAndConditions = ({ showTermsInFooter, onCancelCallback }: Props) => {
  const [isDisplayed, setIsDisplayed] = useState(true)
  const { session: { userName } } = useSessionContext()
  const { refreshChatbotTermsStatus, revision } = useChatbotTermsApi()
  const navigate = useNavigate()
  const location = useLocation()

  const closeTermsModal = () => {
    setIsDisplayed(false)

    const searchParams = new URLSearchParams(location.search)
    searchParams.delete('showTerms')
    const search = searchParams.toString()

    navigate({
      pathname: location.pathname,
      search: search ? `?${search}` : ''
    }, { replace: true })
  }

  const onAccept = async () => {
    try {
      await Api.Chatbot.acceptTerms()
      refreshChatbotTermsStatus()
      setIsDisplayed(false)
    } catch (error) {
      Honeybadger.notify(
        error instanceof Error ? error : new Error('Error accepting terms'),
        { context: { username: userName } }
      )
    }
  }

  const onCancel = () => {
    setIsDisplayed(false)
    onCancelCallback?.()
  }

  const onClose = () => {
    closeTermsModal()
  }

  return (
    <Modal
      isDisplayed={isDisplayed}
      showCloseButton={false}
      closeCallback={showTermsInFooter ? onClose : onCancel}
      variant="lg"
      paperStyles={{
        borderRadius: '20px',
        minHeight: '80vh'
      }}
    >
      <S.Title>Terms & Conditions</S.Title>
      {!showTermsInFooter && <S.Text>Accept these Terms & Conditions to continue:</S.Text>}
      <S.Text>Last updated: {revision}</S.Text>
      <S.Terms>
        <ChatbotTermsAndConditions />
      </S.Terms>

      {!showTermsInFooter
        ? (
          <S.ButtonContainer>
            <Button decoration="secondary" styles={{ button: { marginRight: 15 } }} text="Cancel" onPress={onCancel} />
            <Button decoration="positive" text="I Accept" onPress={onAccept} />
          </S.ButtonContainer>
        )
        : (
          <S.ButtonContainer>
            <Button decoration="positive" text="Close" onPress={onClose} />
          </S.ButtonContainer>
        )}
    </Modal>
  )
}

export default TermsAndConditions

const S = {
  Title: styled.span`
    font-size: 25px;
    font-weight: bold;
    margin-bottom: 16px;
  `,
  Text: styled.p`
    margin-bottom: 25px;
    font-size: 16px;
    color: ${color('chatbotReply')};
  `,
  Terms: styled.div`
    max-height: 62vh;
    overflow-y: auto;
  `,
  ButtonContainer: styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 30px;
  `
}
